import { DATE_FORMAT, IMessage, ListItem, MessageType, Pill, SERVER_DATE_FORMAT, StringUtils } from '@localina/core';
import { EmailIcon, TextAfterIcon, TextBeforeIcon } from '@localina/icons';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
    message: IMessage;
    onClick: (message: IMessage) => void;
}

export const MessageListItem: React.FC<IProps> = (props) => {
    const { t, i18n } = useTranslation();

    const { name, displayTexts, from, to } = props.message;

    const preferred = displayTexts.find((it) => it.language.toLowerCase() === i18n.language?.slice(0, 2));
    const excerpt = (preferred ?? displayTexts.find((it) => it.text))?.text?.trim();

    const parsedFrom = DateTime.fromFormat(from ?? '', SERVER_DATE_FORMAT);
    const parsedTo = DateTime.fromFormat(to ?? '', SERVER_DATE_FORMAT);
    const isLimited = parsedFrom.isValid || parsedTo.isValid;

    const isExpired = parsedTo.isValid && parsedTo.endOf('day') < DateTime.now();

    const validity = () => {
        if (!isLimited) {
            return { tone: 'good' as const, label: t('messages.list.permanent') };
        }
        if (isExpired) {
            return { tone: 'neutral' as const, label: t('messages.list.expired') };
        }
        return {
            tone: 'neutral' as const,
            label: StringUtils.combineStrings(
                [
                    parsedFrom.isValid && parsedFrom.toFormat(DATE_FORMAT),
                    parsedTo.isValid && parsedTo.toFormat(DATE_FORMAT),
                ],
                ' – ',
            ),
        };
    };

    const handleClick = () => {
        props.onClick(props.message);
    };

    const getIcon = () => {
        switch (props.message.type) {
            case MessageType.WELCOME:
                return <TextBeforeIcon />;
            case MessageType.GOODBYE:
                return <TextAfterIcon />;
            case MessageType.CONFIRMATION_MAIL:
            case MessageType.EMAIL:
                return <EmailIcon />;
        }
    };

    const { tone, label } = validity();

    return (
        <ListItem
            classNames="message-listitem"
            leftIcon={<span className="message-listitem__icon">{getIcon()}</span>}
            title={
                <div className="message-listitem__row">
                    <div className="message-listitem__main">
                        <span className="message-listitem__name">{name}</span>
                        <span className="message-listitem__excerpt">{excerpt || t('messages.list.noText')}</span>
                    </div>
                    <div className="message-listitem__meta">
                        {}
                        {displayTexts
                            .filter((it) => it.text)
                            .map((it) => (
                                <Pill key={it.language} tone="teal" label={it.language.toUpperCase()} />
                            ))}
                        <Pill tone={tone} label={label} />
                    </div>
                </div>
            }
            onClick={handleClick}
        />
    );
};
